/**
 * Aeries 504 accommodations import — pure pivot + diff (no Firebase).
 *
 * One row per accommodation in the file. Pivot by STU.CID. The plan verdict
 * is the write gate — never `plan === '504'` alone. Blank incoming never
 * clears. Zero usable bullets is no data, not an empty string.
 *
 * See Documentation/specs/AERIES_504_ACCOMMODATIONS_IMPORT.md.
 */

import {
  assessPlanEvidenceCoverage,
  collectPlanEvidence,
  resolvePlanVerdict,
  schoolYearStartYear,
  summarizeVerdicts,
  toIsoPlanDate
} from './planEvidence.js'
import { isPreferentialSeatingOn, isSeparateSettingOn } from './studentUtils.js'
import { buildAeriesPlanAcceptWrite, isFillableEvalShellStudent } from './aeriesProgramMap.js'
import { FIRST_NAME_ALIAS_HEADERS, firstNameWithAlias } from './aeriesName.js'

export const PRIMARY_DISABILITY_MAX = 100

/** Statewide / section-marker codes — not accommodations. */
export const STATEWIDE_CODES = new Set(['stwreq', 'stacc', 'stwbnr'])

/** Codes that belong on the testing roster. */
export const ASSESSMENT_CODES = new Set(['xtest', 'settin'])

/** Aeries seating codes. Detail is often blank — the label is the wording. */
export const PREFERENTIAL_SEATING_CODES = new Set(['seatn', 'seatp'])

const OTHER_OR_GENERAL = /^(other|general)$/i
const STATEWIDE_LABEL = /are required and identified in this section|are not required/i
// Typo-tolerant for flag DETECTION only — imported 504 text is never rewritten.
// separate/seperate + setting/seting. Requires the word pair; "setting" or
// "separate" alone never flags.
const SEPARATE_SETTING_TEXT = /sep[ae]rate\s+sett?ings?/i
// Aeries SETTIN label and common 504 wording. Same classroom intent as
// "separate setting". Still requires the word pair — "setting" alone never flags.
const ALTERNATIVE_SETTING_TEXT = /alternat(?:e|ive)\s+sett?ings?/i
const FLEXIBLE_SEATING_TEXT = /flexible\s+seat/i
// preferential/preferrential/prefrential + preferred/prefered/preffered.
// Always followed by seat* so "preferred activity" never flags.
const PREFERENTIAL_SEATING_TEXT = /pref(?:err?|r)ential\s+seat|pref\.?\s*seat|preff?err?e?d\s+seat/i
const SEATING_NEAR_TEXT = /seat(?:ing)?(?:\s+student)?\s+near|seating\s+close/i

function cell(value) {
  if (value == null) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  return String(value).trim()
}

function headerForms(value) {
  const lower = String(value || '')
    .toLowerCase()
    .trim()
  const hasQuestion = lower.includes('?')
  const spaced = lower
    .replace(/[^a-z0-9?]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
  const compact = spaced.replace(/[?\s]/g, '')
  return { lower, spaced, compact, hasQuestion }
}

function headerMatches(have, want, { requireQuestion } = {}) {
  if (requireQuestion === true && !have.hasQuestion && !/description/.test(have.lower)) {
    return false
  }
  if (requireQuestion === false && (have.hasQuestion || /description_fap/.test(have.lower))) {
    return false
  }
  if (have.lower === want.lower || have.spaced === want.spaced) return true
  if (want.compact.length > 2 && have.compact === want.compact) return true
  const baseSpaced = have.spaced
    .replace(/\s*\d+$/, '')
    .replace(/\?$/, '')
    .trim()
  const baseCompact = baseSpaced.replace(/[?\s]/g, '')
  if (baseSpaced && baseSpaced === want.spaced) return true
  return want.compact.length > 2 && baseCompact === want.compact
}

function pickHeader(headers, headerMeta, candidates, opts = {}) {
  for (const candidate of candidates) {
    const want = headerForms(candidate)
    for (let i = 0; i < headerMeta.length; i++) {
      if (headerMatches(headerMeta[i], want, opts)) return headers[i]
    }
  }
  return null
}

/**
 * Excel 1900-date serial → YYYY-MM-DD. SheetJS / Excel epoch is 1899-12-30.
 * Only used on date fields — never on SSID / U5 / codes.
 */
export function excelSerialToIso(serial) {
  const n = Number(serial)
  if (!Number.isFinite(n) || n < 20000 || n > 80000) return ''
  const utc = Date.UTC(1899, 11, 30) + Math.round(n) * 86400000
  const date = new Date(utc)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

/**
 * Normalize an Aeries/Excel plan date to YYYY-MM-DD.
 * Accepts ISO, MM/DD/YYYY, Excel serials, and Date objects.
 */
export function normalizeAeriesPlanDate(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const fromSerial = excelSerialToIso(value)
    if (fromSerial) return fromSerial
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const already = toIsoPlanDate(raw)
  if (already) return already
  if (/^\d+(\.\d+)?$/.test(raw)) {
    return excelSerialToIso(Number(raw))
  }
  return ''
}

export function detect504ColumnMapping(headers = []) {
  const headerMeta = headers.map((h) => headerForms(h))
  const mapping = {
    stateStudentId: pickHeader(headers, headerMeta, [
      'state student id',
      'state id',
      'ssid',
      'stu.cid',
      'cid'
    ]),
    lastName: pickHeader(headers, headerMeta, ['last name', 'stu.ln', 'lastname']),
    firstName: pickHeader(headers, headerMeta, ['first name', 'stu.fn', 'firstname']),
    firstNameAlias: pickHeader(headers, headerMeta, FIRST_NAME_ALIAS_HEADERS),
    grade: pickHeader(headers, headerMeta, ['grade', 'grade level', 'stu.gr']),
    u5: pickHeader(headers, headerMeta, ['user5', 'user 5', 'user code 5', 'stu.u5', 'u5']),
    fofRd: pickHeader(headers, headerMeta, ['fof.rd', 'next review date', 'review date']),
    fofDe: pickHeader(headers, headerMeta, ['fof.de', 'description of disability', 'disability']),
    fofSd: pickHeader(headers, headerMeta, ['fof.sd', 'effective date']),
    fofEd: pickHeader(headers, headerMeta, ['fof.ed', 'exit date']),
    area: pickHeader(headers, headerMeta, ['fap.ad', 'area of need'], { requireQuestion: false }),
    areaLabel: pickHeader(
      headers,
      headerMeta,
      ['description_fap_ad', 'description fap ad', 'fap.ad?'],
      { requireQuestion: true }
    ),
    code: pickHeader(headers, headerMeta, ['fap.cd', 'accommodation code'], {
      requireQuestion: false
    }),
    codeLabel: pickHeader(
      headers,
      headerMeta,
      ['description_fap_cd', 'description fap cd', 'fap.cd?'],
      { requireQuestion: true }
    ),
    detail: pickHeader(headers, headerMeta, ['fap.ac', 'accommodation detail']),
    frequency: pickHeader(headers, headerMeta, ['fap.fn', 'frequency'], {
      requireQuestion: false
    }),
    frequencyLabel: pickHeader(
      headers,
      headerMeta,
      ['description_fap_fn', 'description fap fn', 'fap.fn?'],
      { requireQuestion: true }
    ),
    // LOC.SC = state school id (7-digit school part of the CDS), usually
    // exported with the header "School". STU.SC = district site code, backup
    // only (short codes are ignored by the school check). Exact-match
    // normalization keeps bare "school" from matching "School Name".
    schoolCode: pickHeader(headers, headerMeta, [
      'state school id',
      'state school number',
      'loc.sc',
      'school code',
      'cds code',
      'school cds code',
      'school',
      'stu.sc',
      'cds'
    ])
  }

  const mapped = new Set(Object.values(mapping).filter(Boolean))
  const unmapped = headers.filter((h) => !mapped.has(h))
  return { mapping, unmapped }
}

export function mappingHas504Columns(mapping = {}) {
  return Boolean(mapping.stateStudentId && (mapping.detail || mapping.code || mapping.codeLabel))
}

export function isOtherLabel(value) {
  return /^other$/i.test(cell(value))
}

/** FAP.AC stubs that are not the accommodation wording (use the code label). */
const DETAIL_STUB = /^(state(\s|\/|-)*testing|statewide|n\/?a|-)$/i

export function isAccommodationDetailStub(value) {
  return DETAIL_STUB.test(cell(value))
}

export function accommodationBulletText({ detail = '', label = '' } = {}) {
  const ac = cell(detail)
  if (ac && !isAccommodationDetailStub(ac)) return ac
  const lab = cell(label)
  if (lab && !isOtherLabel(lab) && !isAccommodationDetailStub(lab)) return lab
  return ''
}

export function isStatewideMarker({ code = '', label = '', detail = '' } = {}) {
  if (STATEWIDE_CODES.has(cell(code).toLowerCase())) return true
  return STATEWIDE_LABEL.test(`${label} ${detail}`)
}

export function areaSectionLabel({ area = '', areaLabel = '' } = {}) {
  const label = cell(areaLabel) || cell(area)
  if (!label || OTHER_OR_GENERAL.test(label)) return ''
  return label
}

/**
 * Testing → assessment. Classroom / everything else → instruction.
 * Prefer instruction when the row is ambiguous so seating does not land
 * on the testing roster.
 */
export function classifyAccommodationBucket({
  code = '',
  label = '',
  detail = '',
  frequency = ''
} = {}) {
  const freq = cell(frequency).toLowerCase()
  if (/\bduring\s+assess/.test(freq)) return 'assessment'

  const cd = cell(code).toLowerCase()
  if (ASSESSMENT_CODES.has(cd)) return 'assessment'

  const text = `${label} ${detail}`.toLowerCase()
  if (SEPARATE_SETTING_TEXT.test(text) || ALTERNATIVE_SETTING_TEXT.test(text)) return 'assessment'
  if (/\btts\b|text[\s-]*to[\s-]*speech/.test(text) && /test|assess|exam/.test(text)) {
    return 'assessment'
  }
  if (/(extra|extended)\s+time/.test(text) && /test|assess|exam/.test(text)) {
    return 'assessment'
  }
  if (/during\s+(the\s+)?(test|exam|assessment)/.test(text)) return 'assessment'
  return 'instruction'
}

function lineBlob(line = {}) {
  return `${line.label || ''} ${line.detail || ''}`
}

/**
 * Separate setting flag. Classroom FAP wording only: "separate setting" or
 * "alternative setting" (typo-tolerant). STWREQ / state-testing notes never
 * flag. SETTIN code alone does not flag — the imported wording must say it.
 * Extra time is never a setting.
 */
export function lineMentionsSeparateSetting(line = {}) {
  if (isStatewideMarker(line)) return { hit: false, via: '' }
  const text = lineBlob(line)
  if (!SEPARATE_SETTING_TEXT.test(text) && !ALTERNATIVE_SETTING_TEXT.test(text)) {
    return { hit: false, via: '' }
  }
  return { hit: true, via: 'text' }
}

/**
 * Preferential seating for the student-row flag. SEATN/SEATP count even when
 * FAP.AC is blank. "Flexible seating" (movement/sensory) does not.
 */
export function lineMentionsPreferentialSeating(line = {}) {
  if (PREFERENTIAL_SEATING_CODES.has(cell(line.code).toLowerCase())) {
    return { hit: true, via: 'code' }
  }
  const text = lineBlob(line)
  if (FLEXIBLE_SEATING_TEXT.test(text) && !PREFERENTIAL_SEATING_TEXT.test(text)) {
    return { hit: false, via: '' }
  }
  if (PREFERENTIAL_SEATING_TEXT.test(text) || SEATING_NEAR_TEXT.test(text)) {
    return { hit: true, via: 'text' }
  }
  return { hit: false, via: '' }
}

function addFlagSource(set, via) {
  if (via) set.add(via)
}

export function empty504FlagSignals() {
  return {
    separateSetting: false,
    preferentialSeating: false,
    separateSettingVia: new Set(),
    preferentialSeatingVia: new Set(),
    separateSettingTriggers: [],
    preferentialSeatingTriggers: []
  }
}

/** Quote shown on the Flags tab — what in the file triggered the flag. */
export function flagTriggerQuote(line = {}) {
  const bullet = accommodationBulletText(line)
  if (bullet) return bullet
  const detail = cell(line.detail)
  if (detail) return detail
  const label = cell(line.label)
  if (label && !isOtherLabel(label)) return label
  return cell(line.code)
}

function pushFlagTrigger(list, line, via) {
  const quote = flagTriggerQuote(line)
  const key = `${via}|${quote}`
  if (!quote || list.some((t) => `${t.via}|${t.quote}` === key)) return
  list.push({
    quote,
    via,
    code: cell(line.code)
  })
}

export function absorb504FlagLine(signals, line) {
  const sep = lineMentionsSeparateSetting(line)
  if (sep.hit) {
    signals.separateSetting = true
    addFlagSource(signals.separateSettingVia, sep.via)
    pushFlagTrigger(signals.separateSettingTriggers, line, sep.via)
  }
  const pref = lineMentionsPreferentialSeating(line)
  if (pref.hit) {
    signals.preferentialSeating = true
    addFlagSource(signals.preferentialSeatingVia, pref.via)
    pushFlagTrigger(signals.preferentialSeatingTriggers, line, pref.via)
  }
  return signals
}

function flagReason(kind, via) {
  const has = (key) => via instanceof Set && via.has(key)
  if (kind === 'separateSetting') {
    return 'Written in the 504 document. Teachers follow that wording for classwork and tests.'
  }
  if (has('code')) {
    return 'Aeries seating code (SEATN / SEATP) or seating wording in the 504 document.'
  }
  return 'Written in the 504 document (preferential / preferred seating).'
}

export function formatAccommodationField(items = []) {
  if (!items.length) return ''
  const lines = []
  let lastArea = null
  for (const item of items) {
    const area = cell(item.area)
    if (area && area !== lastArea) {
      lines.push(`[${area}]`)
      lastArea = area
    }
    if (item.text) lines.push(item.text)
  }
  return lines.join('\n')
}

export function shortenDisability(text, max = PRIMARY_DISABILITY_MAX) {
  const full = cell(text)
  if (!full) return { field: '', full: '', truncated: false }
  if (full.length <= max) return { field: full, full, truncated: false }
  let field = full.slice(0, max)
  const lastSpace = field.lastIndexOf(' ')
  if (lastSpace >= Math.floor(max * 0.6)) field = field.slice(0, lastSpace)
  return { field, full, truncated: true }
}

function rowVal(row, header) {
  if (!header) return ''
  return row?.[header]
}

function extract504Line(row, mapping) {
  const code = cell(rowVal(row, mapping.code))
  const label = cell(rowVal(row, mapping.codeLabel))
  const detail = cell(rowVal(row, mapping.detail))
  const area = cell(rowVal(row, mapping.area))
  const areaLabel = cell(rowVal(row, mapping.areaLabel))
  const frequency =
    cell(rowVal(row, mapping.frequency)) || cell(rowVal(row, mapping.frequencyLabel))
  return { code, label, detail, area, areaLabel, frequency }
}

function studentNameFromExport(exportData) {
  return `${exportData.firstName || ''} ${exportData.lastName || ''}`.trim()
}

function studentNameFromDoc(existing, exportData) {
  const fromApp = [
    existing?.data?.app?.studentData?.firstName,
    existing?.data?.app?.studentData?.lastName
  ]
    .filter(Boolean)
    .join(' ')
  return fromApp || studentNameFromExport(exportData)
}

/**
 * Pivot a 504 export (one row per accommodation) to one record per SSID.
 */
export function pivotTo504Roster(rows = [], mapping = {}) {
  const students = {}
  const warnings = []
  let skippedEmpty = 0
  let skippedStatewide = 0
  let skippedNoSsid = 0

  for (const raw of rows) {
    const ssid = cell(rowVal(raw, mapping.stateStudentId))
    if (!ssid) {
      skippedNoSsid++
      continue
    }

    if (!students[ssid]) {
      const fofRd = normalizeAeriesPlanDate(rowVal(raw, mapping.fofRd))
      const fofSd = normalizeAeriesPlanDate(rowVal(raw, mapping.fofSd))
      const fofEd = normalizeAeriesPlanDate(rowVal(raw, mapping.fofEd))
      students[ssid] = {
        ssid,
        firstName: firstNameWithAlias(
          cell(rowVal(raw, mapping.firstName)),
          cell(rowVal(raw, mapping.firstNameAlias))
        ),
        lastName: cell(rowVal(raw, mapping.lastName)),
        grade: cell(rowVal(raw, mapping.grade)),
        u5: cell(rowVal(raw, mapping.u5)),
        fofSd,
        fofEd,
        fofRd,
        fofDe: cell(rowVal(raw, mapping.fofDe)),
        in504File: true,
        lines: [],
        snapshot: [],
        skippedEmpty: 0,
        skippedStatewide: 0,
        flagSignals: empty504FlagSignals()
      }
    }

    const line = extract504Line(raw, mapping)
    absorb504FlagLine(students[ssid].flagSignals, line)
    if (isStatewideMarker(line)) {
      skippedStatewide++
      students[ssid].skippedStatewide++
      continue
    }
    const text = accommodationBulletText(line)
    if (!text) {
      skippedEmpty++
      students[ssid].skippedEmpty++
      continue
    }

    const area = areaSectionLabel(line)
    const bucket = classifyAccommodationBucket({ ...line, detail: text })
    const item = { area, text, bucket, code: line.code }
    students[ssid].lines.push(item)
    students[ssid].snapshot.push({
      code: line.code,
      label: line.label,
      detail: line.detail,
      area: area || line.areaLabel || line.area,
      frequency: line.frequency
    })
  }

  for (const stu of Object.values(students)) {
    const seen = new Set()
    const unique = []
    const uniqueSnap = []
    stu.lines.forEach((item, i) => {
      const key = `${item.code}|${item.text}`
      if (seen.has(key)) return
      seen.add(key)
      unique.push(item)
      uniqueSnap.push(stu.snapshot[i])
    })
    stu.lines = unique
    stu.snapshot = uniqueSnap
    stu.instructionText = formatAccommodationField(unique.filter((l) => l.bucket === 'instruction'))
    stu.assessmentText = formatAccommodationField(unique.filter((l) => l.bucket === 'assessment'))
    stu.hasUsableBullets = unique.length > 0
    const flags = stu.flagSignals || empty504FlagSignals()
    stu.proposeFlag1 = flags.preferentialSeating
    stu.proposeFlag2 = flags.separateSetting
    stu.flag1Reason = flags.preferentialSeating
      ? flagReason('preferentialSeating', flags.preferentialSeatingVia)
      : ''
    stu.flag2Reason = flags.separateSetting
      ? flagReason('separateSetting', flags.separateSettingVia)
      : ''
    stu.flag1Triggers = flags.preferentialSeatingTriggers
    stu.flag2Triggers = flags.separateSettingTriggers
  }

  if (skippedNoSsid) {
    warnings.push({ message: `${skippedNoSsid} row(s) had no SSID (STU.CID) and were skipped.` })
  }

  return {
    students,
    warnings,
    stats: {
      uniqueStudents: Object.keys(students).length,
      withAccommodations: Object.values(students).filter((s) => s.hasUsableBullets).length,
      skippedEmpty,
      skippedStatewide,
      skippedNoSsid
    }
  }
}

function aeriesRowForEvidence(exportData) {
  // Presence in this file means the query joined through FOF. If header
  // columns were omitted, still mark the plan as existing so the student
  // cannot silently drop to tagOnly / none.
  const fofSd = exportData.fofSd || (exportData.in504File ? '(in 504 file)' : '')
  return {
    'STU.U5': exportData.u5,
    'FOF.SD': fofSd,
    'FOF.ED': exportData.fofEd || '',
    'FOF.RD': exportData.fofRd || '',
    'FOF.DE': exportData.fofDe || ''
  }
}

function currentAccommodations(studentDoc) {
  const acc = studentDoc?.app?.accommodations || {}
  return {
    instruction: cell(acc.instruction),
    assessment: cell(acc.assessment)
  }
}

function currentFlags(studentDoc) {
  const flags = studentDoc?.app?.flags || {}
  return {
    flag1: isPreferentialSeatingOn(flags, studentDoc),
    flag2: isSeparateSettingOn(flags, studentDoc)
  }
}

/**
 * One Flags-tab row. Only a change is listed:
 *   file has wording + flag off → Turn on (accept)
 *   flag on + file has no wording → Unflag (keep)
 * Already-correct matches (wording + flag on) are skipped.
 */
export function makeFlagReview({
  ssid,
  docId,
  studentName,
  field,
  label,
  alreadyOn,
  incomingOn,
  writeAllowed,
  blocked,
  reason,
  triggers = [],
  currentBackup = null,
  preview = null
}) {
  const on = !!alreadyOn
  const match = !!incomingOn
  if (match && on) return null
  if (!match && !on) return null
  const recommendUnflag = on && !match
  return {
    key: `${ssid}|${field}`,
    ssid,
    docId,
    studentName,
    field,
    label,
    kind: 'flag',
    alreadyOn: on,
    incomingOn: match,
    recommend: recommendUnflag ? 'unflag' : 'on',
    triggers: match ? triggers : [],
    reason: recommendUnflag
      ? `This flag is on in the app, but the 504 file has no ${String(label || 'matching').toLowerCase()} wording.`
      : reason,
    defaultDecision: recommendUnflag ? 'keep' : 'accept',
    canAccept: writeAllowed && !blocked,
    writeAllowed,
    blocked: !!blocked,
    preview: preview || {
      appInstruction: '',
      appAssessment: '',
      fileInstruction: '',
      fileAssessment: ''
    },
    // Full snapshot of the student's current values. Flag-only writes use it
    // for the undo backup so undo never wipes accommodations or plan504.
    currentBackup
  }
}

function hasText(value) {
  return cell(value) !== ''
}

function makeChange({
  field,
  label,
  currentValue,
  incomingValue,
  writeAllowed,
  coverageUsable,
  reason
}) {
  const incoming = cell(incomingValue)
  const current = cell(currentValue)
  if (!incoming) return null
  if (incoming === current) return null

  const currentIsBlank = !hasText(current)
  const canAcceptIfAllowed = coverageUsable || currentIsBlank
  const defaultDecision = currentIsBlank ? 'accept' : 'keep'
  return {
    field,
    label,
    kind: currentIsBlank ? 'fill' : 'update',
    currentValue: current,
    incomingValue: incoming,
    currentRaw: current,
    incomingRaw: incoming,
    defaultDecision,
    canAcceptIfAllowed,
    canAccept: writeAllowed && canAcceptIfAllowed,
    // Field-specific note from the caller (e.g. disability truncation).
    // applyCoverage composes the final reason from this, the meeting-held
    // signal, and the coverage gate.
    note: reason || '',
    reason:
      reason ||
      (!coverageUsable && !currentIsBlank
        ? 'Dates in this file are sparse — recency cannot decide. Existing text is not overwritten.'
        : currentIsBlank
          ? 'App field is empty'
          : 'Recommended: keep the app text')
  }
}

function schoolYearLabel(now, schoolYear) {
  if (schoolYear) return schoolYear
  const start = schoolYearStartYear(now)
  return `${start}-${start + 1}`
}

/**
 * Build the approve / blocked / report lists for a 504 file.
 *
 * @param {object} args
 * @param {object} args.students - pivoted export, keyed by SSID
 * @param {Map<string, {id: string, data: object}>} args.studentMap
 * @param {Date} [args.now]
 * @param {string} [args.schoolYear]
 */
export function buildAeries504Diff({
  students = {},
  studentMap = new Map(),
  now = new Date(),
  schoolYear = ''
} = {}) {
  const year = schoolYearLabel(now, schoolYear)
  const evidenceOpts = { now, schoolYear: year }

  const signalsList = []
  const resolved = []
  const unmatched = []
  const blocked = []
  const reported = []
  const diffStudents = []
  const unchanged = []
  const skippedNoData = []
  const flagReviews = []
  let flagsAlreadyMatchFlag1 = 0
  let flagsAlreadyMatchFlag2 = 0

  for (const [ssid, exportData] of Object.entries(students)) {
    const existing = studentMap.get(String(ssid).trim())
    const aeriesRow = aeriesRowForEvidence(exportData)
    const signals = collectPlanEvidence({
      ssid,
      aeriesRow,
      studentDoc: existing?.data || null,
      ...evidenceOpts
    })
    signalsList.push(signals)

    if (!existing) {
      unmatched.push({
        ssid,
        name: studentNameFromExport(exportData),
        hasAccommodations: Boolean(exportData.hasUsableBullets)
      })
      continue
    }

    const verdict = resolvePlanVerdict(signals)
    resolved.push(verdict)

    const studentName = studentNameFromDoc(existing, exportData)
    const appPlan = cell(existing.data?.app?.studentData?.plan)
    const appPlanStatus = cell(existing.data?.app?.studentData?.planStatus)
    const appCaseManagerId = cell(existing.data?.app?.studentData?.caseManagerId)
    const fillableShell = isFillableEvalShellStudent({
      appPlan,
      appPlanStatus,
      appCaseManagerId
    })
    const current = currentAccommodations(existing.data)
    const flagsNow = currentFlags(existing.data)
    const currentReview =
      normalizeAeriesPlanDate(existing.data?.app?.dates?.reviewDate) ||
      cell(existing.data?.app?.dates?.reviewDate)
    const currentDisability = cell(existing.data?.app?.studentData?.primaryDisability)
    const disability = shortenDisability(exportData.fofDe)

    // Meeting-held signal: the app's plan review date and FOF.RD are the same
    // field (the annual 504 review). When the file's date is later, a meeting
    // was held and Aeries was updated after the app — Aeries becomes the
    // default source of truth for this student's text fields. Equal dates
    // mean the app is in sync (its text wins by default). ISO strings compare
    // lexicographically. Blank on either side proves nothing.
    const fileReviewIso = normalizeAeriesPlanDate(exportData.fofRd)
    const appReviewIso = normalizeAeriesPlanDate(existing.data?.app?.dates?.reviewDate)
    const meetingHeld = Boolean(fileReviewIso && appReviewIso && fileReviewIso > appReviewIso)

    const base = {
      ssid,
      docId: existing.id,
      studentName,
      exportData,
      meetingHeld,
      meetingSignal: { fileReviewDate: fileReviewIso, appReviewDate: appReviewIso },
      signals,
      verdict: verdict.verdict,
      verdictReason: verdict.reason,
      blocked: verdict.blocked,
      write504: verdict.write504,
      report: verdict.report,
      drivingDate: verdict.drivingDate,
      fillableShell,
      appPlan,
      appPlanStatus,
      appCaseManagerId,
      incoming: {
        instruction: exportData.instructionText || '',
        assessment: exportData.assessmentText || '',
        reviewDate: exportData.fofRd || '',
        primaryDisability: disability.field,
        disabilityFull: disability.full,
        disabilityTruncated: disability.truncated,
        snapshot: exportData.snapshot || [],
        flag1: Boolean(exportData.proposeFlag1),
        flag2: Boolean(exportData.proposeFlag2)
      },
      current: {
        instruction: current.instruction,
        assessment: current.assessment,
        reviewDate: currentReview,
        primaryDisability: currentDisability,
        flag1: flagsNow.flag1,
        flag2: flagsNow.flag2,
        accommodations504: existing.data?.aeries?.accommodations504 || [],
        plan504: existing.data?.aeries?.plan504 || null
      }
    }

    if (verdict.report) {
      reported.push({
        ssid,
        studentName,
        verdict: verdict.verdict,
        report: verdict.report,
        reason: verdict.reason,
        drivingDate: verdict.drivingDate,
        write504: verdict.write504,
        blocked: verdict.blocked
      })
    }

    const coveragePlaceholder = true
    const changes = [
      makeChange({
        field: 'instruction',
        label: 'Instruction accommodations',
        currentValue: current.instruction,
        incomingValue: exportData.instructionText,
        writeAllowed: verdict.write504,
        coverageUsable: coveragePlaceholder,
        reason: ''
      }),
      makeChange({
        field: 'assessment',
        label: 'Assessment accommodations',
        currentValue: current.assessment,
        incomingValue: exportData.assessmentText,
        writeAllowed: verdict.write504,
        coverageUsable: coveragePlaceholder,
        reason: ''
      }),
      makeChange({
        field: 'reviewDate',
        label: '504 review date',
        currentValue: currentReview,
        incomingValue: exportData.fofRd,
        writeAllowed: verdict.write504,
        coverageUsable: coveragePlaceholder,
        reason: ''
      }),
      makeChange({
        field: 'primaryDisability',
        label: 'Description of disability',
        currentValue: currentDisability,
        incomingValue: disability.field,
        writeAllowed: verdict.write504,
        coverageUsable: coveragePlaceholder,
        reason: disability.truncated
          ? 'Longer than 100 characters — full text is kept on the Aeries 504 snapshot'
          : ''
      })
    ].filter(Boolean)

    const flagRowArgs = {
      ssid,
      docId: existing.id,
      studentName,
      writeAllowed: verdict.write504,
      blocked: verdict.blocked,
      currentBackup: build504Backup(base.current),
      preview: {
        appInstruction: current.instruction,
        appAssessment: current.assessment,
        fileInstruction: exportData.instructionText || '',
        fileAssessment: exportData.assessmentText || ''
      }
    }
    const studentFlags = [
      makeFlagReview({
        ...flagRowArgs,
        field: 'flag1',
        label: 'Preferential seating',
        alreadyOn: flagsNow.flag1,
        incomingOn: exportData.proposeFlag1,
        reason: exportData.flag1Reason,
        triggers: exportData.flag1Triggers || []
      }),
      makeFlagReview({
        ...flagRowArgs,
        field: 'flag2',
        label: 'Separate setting',
        alreadyOn: flagsNow.flag2,
        incomingOn: exportData.proposeFlag2,
        reason: exportData.flag2Reason,
        triggers: exportData.flag2Triggers || []
      })
    ].filter(Boolean)
    if (verdict.write504 || verdict.blocked) {
      if (exportData.proposeFlag1 && flagsNow.flag1) flagsAlreadyMatchFlag1 += 1
      if (exportData.proposeFlag2 && flagsNow.flag2) flagsAlreadyMatchFlag2 += 1
    }
    flagReviews.push(...studentFlags)

    const noIncomingData =
      !exportData.hasUsableBullets &&
      !exportData.fofRd &&
      !disability.field &&
      !exportData.proposeFlag1 &&
      !exportData.proposeFlag2 &&
      !flagsNow.flag1 &&
      !flagsNow.flag2

    // Active-plan rows stay visible even when there is nothing to write.
    // skippedNoData is only for writeable students with empty FAP shells.
    if (verdict.blocked) {
      blocked.push({
        ...base,
        changes: changes.map((c) => ({ ...c, canAccept: false }))
      })
      continue
    }
    if (!verdict.write504) {
      continue
    }
    if (noIncomingData && !fillableShell) {
      skippedNoData.push({ ssid, studentName })
      continue
    }
    if (!changes.length && !fillableShell) {
      unchanged.push({ ssid, studentName })
    } else {
      diffStudents.push({ ...base, changes })
    }
  }

  const coverage = assessPlanEvidenceCoverage(signalsList)
  const applyCoverage = (row) => {
    // Meeting-held flips the default to accept, but only changes the DEFAULT:
    // the coverage gate and blocking still decide whether accept is allowed,
    // and a blocked student keeps the cautious default even after override.
    const meetingReason = row.meetingHeld
      ? `504 review date moved (app ${row.meetingSignal.appReviewDate} → file ${row.meetingSignal.fileReviewDate}) — a meeting looks held since the app was last updated. Use Aeries is preselected.`
      : ''
    row.changes = row.changes
      .map((change) => {
        const currentIsBlank = !hasText(change.currentValue)
        const canAcceptIfAllowed = coverage.usable || currentIsBlank
        const writeAllowed = row.write504
        const aeriesIsDefault =
          currentIsBlank || (row.meetingHeld && canAcceptIfAllowed && !row.blocked)
        return {
          ...change,
          canAcceptIfAllowed,
          canAccept: writeAllowed && canAcceptIfAllowed && !row.blocked,
          defaultDecision: aeriesIsDefault ? 'accept' : 'keep',
          reason:
            !coverage.usable && !currentIsBlank
              ? coverage.reason
              : currentIsBlank
                ? change.note || 'App field is empty'
                : change.note || meetingReason || 'Recommended: keep the app text'
        }
      })
      .filter(Boolean)
    return row
  }

  const gatedStudents = diffStudents
    .map(applyCoverage)
    .filter((r) => r.changes.length || r.fillableShell)
  const gatedBlocked = blocked.map(applyCoverage)

  const flags = flagReviews
    .filter((row) => row.writeAllowed || row.blocked)
    .sort((a, b) => {
      if (a.recommend !== b.recommend) return a.recommend === 'unflag' ? -1 : 1
      return String(a.studentName || '').localeCompare(String(b.studentName || ''))
    })

  return {
    students: gatedStudents,
    blocked: gatedBlocked,
    flags,
    reported,
    unmatched,
    unchanged,
    skippedNoData,
    coverage,
    verdicts: summarizeVerdicts(resolved),
    summary: {
      studentsInFile: Object.keys(students).length,
      studentsMatched: Object.keys(students).length - unmatched.length,
      studentsNew: unmatched.length,
      studentsWithChanges: gatedStudents.length,
      fillableShells: [...gatedStudents, ...gatedBlocked].filter((row) => row.fillableShell).length,
      flagRows: flags.length,
      flagsToConfirm: flags.filter((f) => f.recommend === 'on').length,
      flagsToUnflag: flags.filter((f) => f.recommend === 'unflag').length,
      flagsAlreadyMatch: flagsAlreadyMatchFlag1 + flagsAlreadyMatchFlag2,
      flagsAlreadyMatchFlag1,
      flagsAlreadyMatchFlag2,
      blocked: gatedBlocked.length,
      reported: reported.length,
      unchanged: unchanged.length,
      skippedNoData: skippedNoData.length,
      meetingsHeld: gatedStudents.filter((s) => s.meetingHeld).length,
      totalChanges: gatedStudents.reduce((n, s) => n + s.changes.length, 0)
    }
  }
}

/**
 * Blocked students stay out of the approve table until overridden.
 * After override they keep field-level keep/accept (and the coverage gate).
 */
export function visible504ApproveStudents(diff, overrides = {}) {
  const extra = (diff?.blocked || [])
    .filter((stu) => overrides[stu.ssid])
    .map((stu) => ({
      ...stu,
      changes: (stu.changes || []).map((change) => ({
        ...change,
        canAccept: change.canAcceptIfAllowed !== false
      }))
    }))
  return [...(diff?.students || []), ...extra]
}

/** Flags tab — blocked rows become confirmable after Import anyway. */
export function visible504FlagReviews(diff, overrides = {}) {
  return (diff?.flags || []).map((row) => {
    const unlocked = row.blocked && overrides[row.ssid]
    return {
      ...row,
      canAccept: row.writeAllowed || unlocked
    }
  })
}

function decisionFor(stu, change, decisions) {
  const key = `${stu.ssid}|${change.field}`
  return decisions[key]?.decision || change.defaultDecision
}

function isAccepted(stu, change, decisions, overrides) {
  const allowed = stu.write504 || (stu.blocked && overrides[stu.ssid])
  if (!allowed) return false
  if (change.canAcceptIfAllowed === false) return false
  return decisionFor(stu, change, decisions) === 'accept'
}

export function build504Backup(current = {}) {
  return {
    instruction: current.instruction || '',
    assessment: current.assessment || '',
    reviewDate: current.reviewDate || '',
    primaryDisability: current.primaryDisability || '',
    flag1: !!current.flag1,
    flag2: !!current.flag2,
    accommodations504: current.accommodations504 || [],
    plan504: current.plan504 || null
  }
}

/**
 * Collect accepted writes. Blocked rows need overrides[ssid].
 * supersededByIep / tagOnly never write, even if someone ticks a box.
 */
function shell504AcceptResult(stu, caseManagers, coordinatorId) {
  if (!stu?.fillableShell) return { acceptPlan: null, holdForCoordinator: false }
  const result = buildAeriesPlanAcceptWrite({
    appPlan: stu.appPlan,
    appPlanStatus: stu.appPlanStatus,
    appCaseManagerId: stu.appCaseManagerId,
    exportValue: '504',
    coordinatorId,
    caseManagers
  })
  if (result.needsCoordinatorPick) {
    return { acceptPlan: null, holdForCoordinator: true }
  }
  if (!result.ok || !result.shell504Accept) {
    return { acceptPlan: null, holdForCoordinator: false }
  }
  return { acceptPlan: result.studentData, holdForCoordinator: false }
}

export function collect504Writes(diff, decisions = {}, overrides = {}, options = {}) {
  const writes = []
  let kept = 0
  let blockedHeld = 0
  const caseManagers = options.caseManagers || []
  const coordinatorId = options.coordinatorId || ''
  const heldForCoordinator = new Set()

  const rows = [...(diff?.students || []), ...(diff?.blocked || [])]
  for (const stu of rows) {
    if (stu.blocked && !overrides[stu.ssid]) {
      blockedHeld++
      continue
    }
    if (!stu.write504 && !(stu.blocked && overrides[stu.ssid])) {
      continue
    }

    const { acceptPlan, holdForCoordinator } = shell504AcceptResult(
      stu,
      caseManagers,
      coordinatorId
    )
    if (holdForCoordinator) {
      heldForCoordinator.add(stu.ssid)
      continue
    }

    const accepted = {}
    for (const change of stu.changes || []) {
      if (isAccepted(stu, change, decisions, overrides)) {
        accepted[change.field] = change.incomingRaw
      } else if (change.incomingRaw && !change.alreadyOn) {
        kept++
      }
    }

    const hasAccWrite = accepted.instruction != null || accepted.assessment != null
    const hasHeaderWrite = accepted.reviewDate != null || accepted.primaryDisability != null
    if (!hasAccWrite && !hasHeaderWrite && !acceptPlan) continue

    const incoming = stu.incoming || {}
    const snapshot = incoming.snapshot || []
    writes.push({
      ssid: stu.ssid,
      docId: stu.docId,
      studentName: stu.studentName,
      acceptPlan,
      instruction: accepted.instruction,
      assessment: accepted.assessment,
      reviewDate: accepted.reviewDate,
      primaryDisability: accepted.primaryDisability,
      flag1: undefined,
      flag2: undefined,
      disabilityFull: incoming.disabilityFull || accepted.primaryDisability || '',
      snapshot: hasAccWrite || snapshot.length ? snapshot : undefined,
      plan504: {
        effectiveDate:
          stu.exportData?.fofSd && stu.exportData.fofSd !== '(in 504 file)'
            ? stu.exportData.fofSd
            : '',
        nextReviewDate: incoming.reviewDate || stu.exportData?.fofRd || '',
        description: incoming.disabilityFull || stu.exportData?.fofDe || ''
      },
      backup: {
        ...build504Backup(stu.current),
        fields: Object.keys(accepted),
        ...(acceptPlan
          ? {
              previousPlan: {
                plan: stu.appPlan || '',
                planStatus: stu.appPlanStatus || '',
                caseManagerId: stu.appCaseManagerId || ''
              }
            }
          : {})
      },
      verdict: stu.verdict
    })
  }

  applyFlagReviewsToWrites(
    writes,
    diff?.flags || [],
    decisions,
    overrides,
    (n) => {
      kept += n
    },
    heldForCoordinator
  )

  return {
    writes,
    kept,
    blockedHeld,
    unmatched: diff?.unmatched?.length || 0
  }
}

function flagDecisionValue(row, decisions, overrides) {
  const allowed = row.writeAllowed || (row.blocked && overrides[row.ssid])
  if (!allowed) return undefined
  const decision = decisions[row.key]?.decision || row.defaultDecision
  const wantOn = decision === 'accept'
  if (wantOn && !row.alreadyOn) return true
  if (!wantOn && row.alreadyOn) return false
  return undefined
}

function applyFlagReviewsToWrites(
  writes,
  flags,
  decisions,
  overrides,
  onKept,
  heldForCoordinator = new Set()
) {
  const bySsid = new Map(writes.map((w) => [w.ssid, w]))
  let keptFlags = 0
  for (const row of flags) {
    if (heldForCoordinator.has(row.ssid)) continue
    const value = flagDecisionValue(row, decisions, overrides)
    if (value === undefined) {
      if (row.alreadyOn) continue
      if ((decisions[row.key]?.decision || row.defaultDecision) === 'keep') keptFlags++
      continue
    }
    let write = bySsid.get(row.ssid)
    if (!write) {
      // Flag-only student: no text / header write. flagOnly makes the
      // payload skip aeries.plan504 so a flag confirm never clears it.
      write = {
        ssid: row.ssid,
        docId: row.docId,
        studentName: row.studentName,
        flagOnly: true,
        instruction: undefined,
        assessment: undefined,
        reviewDate: undefined,
        primaryDisability: undefined,
        flag1: undefined,
        flag2: undefined,
        disabilityFull: '',
        snapshot: undefined,
        plan504: {},
        backup: {
          ...(row.currentBackup || build504Backup({})),
          fields: []
        },
        verdict: ''
      }
      writes.push(write)
      bySsid.set(row.ssid, write)
    }
    write[row.field] = value
    if (!write.backup.fields.includes(row.field)) write.backup.fields.push(row.field)
  }
  onKept(keptFlags)
}
